import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faFileLines } from "@fortawesome/free-solid-svg-icons";
import { ReadOnlyPythonCode } from "../student-ide/ReadOnlyPythonCode";
import { StudentIdeTourPanel } from "../student-ide/StudentIdeTourPanel";
import { IdeOutputPanel } from "../python-runtime/IdeOutputPanel";
import type { ConsoleLine } from "../python-runtime/usePythonRuntime";
import {
  DEMO_ADVANCED_CONSOLE,
  DEMO_ADVANCED_FILES,
  DEMO_SIMPLE_CODE,
  DEMO_SIMPLE_CONSOLE,
  DEMO_SIMPLE_TOUR,
} from "./demoData";

/** The real console panel wired with inert (read-only) handlers. */
function StaticConsole({ lines }: { lines: readonly ConsoleLine[] }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <IdeOutputPanel
      lines={lines}
      phase="ready"
      inputPrompt=""
      inputDraft=""
      onInputDraftChange={() => {}}
      onSubmitInput={() => {}}
      consoleInputRef={inputRef}
      clearConsoleOnRun
      onClearConsoleOnRunChange={() => {}}
      outputTab="console"
      onOutputTabChange={() => {}}
      turtleHtml={null}
      turtleTabVisible={false}
    />
  );
}

function EditorFrame({ filename, code }: { filename: string; code: string }) {
  return (
    <div className="ide-editor-panel landing-ide-editor">
      <div className="ide-panel-label landing-ide-editor-head">
        <span>
          <FontAwesomeIcon icon={faFileLines} /> {filename}
        </span>
        <span className="btn primary ide-btn tight landing-ide-run" aria-hidden="true">
          <FontAwesomeIcon icon={faPlay} /> Run
        </span>
      </div>
      <ReadOnlyPythonCode code={code} className="landing-ide-code" maxHeight="20rem" />
    </div>
  );
}

function SimpleIde() {
  return (
    <div className="landing-ide-grid landing-ide-grid--simple">
      <EditorFrame filename="main.py" code={DEMO_SIMPLE_CODE} />
      <StaticConsole lines={DEMO_SIMPLE_CONSOLE} />
      <StudentIdeTourPanel steps={DEMO_SIMPLE_TOUR} />
    </div>
  );
}

function AdvancedIde() {
  const [active, setActive] = useState(DEMO_ADVANCED_FILES[0]!.path);
  const file = DEMO_ADVANCED_FILES.find((f) => f.path === active) ?? DEMO_ADVANCED_FILES[0]!;
  return (
    <div className="landing-ide-grid landing-ide-grid--advanced">
      <nav className="landing-ide-files" aria-label="Project files">
        <p className="ide-panel-label">Files</p>
        <ul>
          {DEMO_ADVANCED_FILES.map((f) => (
            <li key={f.path}>
              <button
                type="button"
                className={`landing-ide-file${f.path === active ? " landing-ide-file--active" : ""}`}
                onClick={() => setActive(f.path)}
              >
                <FontAwesomeIcon icon={faFileLines} fixedWidth /> {f.path}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <EditorFrame filename={file.path} code={file.code} />
      <StaticConsole lines={DEMO_ADVANCED_CONSOLE} />
    </div>
  );
}

/** Static, read-only showcase of the simple and advanced classroom IDEs. */
export default function IdeShowcase() {
  const [mode, setMode] = useState<"simple" | "advanced">("simple");
  return (
    <div className="landing-ide">
      <div className="landing-ide-toggle" role="tablist" aria-label="IDE mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "simple"}
          className={`landing-ide-toggle-btn${mode === "simple" ? " landing-ide-toggle-btn--active" : ""}`}
          onClick={() => setMode("simple")}
        >
          Beginner IDE
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "advanced"}
          className={`landing-ide-toggle-btn${mode === "advanced" ? " landing-ide-toggle-btn--active" : ""}`}
          onClick={() => setMode("advanced")}
        >
          Advanced multi-file IDE
        </button>
      </div>
      {mode === "simple" ? <SimpleIde /> : <AdvancedIde />}
    </div>
  );
}
